import React from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import { useConfiguration } from '../../context/configurationContext';
import { ACCOUNT_SETTINGS_PAGES } from '../../routing/routeConfiguration';
import { getCurrentUserTypeRoles, isAdminUser } from '../../util/userHelpers';
import { getCurrentUserFirstListing } from '../../ducks/user.duck';
import {
  LISTING_PAGE_PARAM_TYPE_EDIT,
  LISTING_PAGE_PARAM_TYPE_NEW,
  NEW_LISTING_DRAFT_ID,
  NEW_LISTING_DRAFT_SLUG,
} from '../../util/urlHelpers';
import { LinkTabNavHorizontal, NamedLink } from '../../components';

import { DashboardSidepanelIcon } from './DashboardSidepanelIcons';
import css from './UserNav.module.css';

export const DASHBOARD_TAB_KEYS = {
  OVERVIEW: 'overview',
  BOOKINGS: 'bookings',
  PAYMENTS: 'payments',
  MESSAGES: 'messages',
};

export const DASHBOARD_TAB_KEYS_PROVIDER = {
  OVERVIEW: 'overview',
  EDIT_PROFILE: 'editProfile',
  AVAILABILITY: 'availability',
  PRICING: 'pricing',
  BOOKINGS: 'bookings',
  EARNINGS: 'earnings',
  MESSAGES: 'messages',
};

// Listing wizard tabs the dashboard exposes for an expert's own listing. The admin
// listing editor (AdminEditListingPage) shows exactly the same tabs.
export const EDIT_LISTING_TABS = [
  { tab: 'details', labelId: 'DashboardPage.tabEditProfile' },
  { tab: 'availability', labelId: 'DashboardPage.tabAvailability' },
  { tab: 'pricing', labelId: 'DashboardPage.tabPricing' },
];

const ACCOUNT_SUB_PAGES = [
  {
    name: 'ContactDetailsPage',
    labelId: 'LayoutWrapperAccountSettingsSideNav.contactDetailsTabTitle',
  },
  {
    name: 'GoogleCalendarSettingsPage',
    labelId: 'LayoutWrapperAccountSettingsSideNav.googleCalendarTabTitle',
    providerOnly: true,
  },
  {
    name: 'StripePayoutPage',
    labelId: 'LayoutWrapperAccountSettingsSideNav.paymentsTabTitle',
    payoutOnly: true,
  },
  {
    name: 'PaymentMethodsPage',
    labelId: 'LayoutWrapperAccountSettingsSideNav.paymentMethodsTabTitle',
    paymentMethodsOnly: true,
  },
  { name: 'PasswordChangePage', labelId: 'LayoutWrapperAccountSettingsSideNav.passwordTabTitle' },
  {
    name: 'ManageAccountPage',
    labelId: 'LayoutWrapperAccountSettingsSideNav.manageAccountTabTitle',
  },
];

const DashboardSidepanel = props => {
  const {
    activeTab,
    onTabChange,
    currentPage,
    className,
    rootClassName,
    showAccountPayoutDetails,
    showAccountPaymentMethods,
    currentUser,
    editListingParams,
    currentEditTab,
    disabledEditTabs = [],
  } = props;
  const intl = useIntl();
  const config = useConfiguration();
  const currentUserSaleNotificationCount = useSelector(
    state => state?.user?.currentUserSaleNotificationCount || 0
  );
  const currentUserOrderNotificationCount = useSelector(
    state => state?.user?.currentUserOrderNotificationCount || 0
  );
  const currentUserSaleMessageCount = useSelector(
    state => state?.user?.currentUserSaleMessageCount || 0
  );
  const currentUserOrderMessageCount = useSelector(
    state => state?.user?.currentUserOrderMessageCount || 0
  );
  // Pages that don't pass editListingParams (e.g. the admin pages) still link to the
  // provider's own listing: TopbarContainer fetches it into the store on mount, exactly
  // like the dashboard and account pages derive it.
  const currentUserFirstListing = useSelector(getCurrentUserFirstListing);
  const resolvedEditListingParams =
    editListingParams ||
    (currentUserFirstListing?.id && currentUserFirstListing?.slug
      ? { id: currentUserFirstListing.id, slug: currentUserFirstListing.slug }
      : null);
  const roles = getCurrentUserTypeRoles(config, currentUser);
  const isProvider = !!(currentUser && roles?.provider);
  const hasBookingsNotifications =
    currentUserSaleNotificationCount > 0 || currentUserOrderNotificationCount > 0;
  const hasMessagesNotifications =
    currentUserSaleMessageCount > 0 || currentUserOrderMessageCount > 0;
  const classes = classNames(css.dashboardSidepanel, rootClassName, className);
  const isProfileSettingsActive = currentPage === 'ProfileSettingsPage';
  const isAccountSettingsActive = ACCOUNT_SETTINGS_PAGES.includes(currentPage);
  const isOnDashboard = currentPage === 'DashboardPage';
  const useDashboardTabsAsLinks = !isOnDashboard || activeTab == null || onTabChange == null;

  // Edit Listing links: use existing listing (edit) when available, otherwise new listing flow (draft)
  const hasEditListing =
    resolvedEditListingParams?.id &&
    resolvedEditListingParams?.slug &&
    resolvedEditListingParams?.id !== NEW_LISTING_DRAFT_ID;
  const editListingLinkParams = hasEditListing
    ? {
        id: resolvedEditListingParams.id,
        slug: resolvedEditListingParams.slug,
        type: LISTING_PAGE_PARAM_TYPE_EDIT,
      }
    : { id: NEW_LISTING_DRAFT_ID, slug: NEW_LISTING_DRAFT_SLUG, type: LISTING_PAGE_PARAM_TYPE_NEW };

  const editListingLink = (tab, labelId, iconName, selected = false) => (
    <NamedLink
      key={tab}
      name="EditListingPage"
      params={{
        ...editListingLinkParams,
        tab,
      }}
      className={classNames(css.dashboardSidepanelButton, css.dashboardSidepanelLink, {
        [css.dashboardSidepanelButtonActive]: selected,
      })}
    >
      <DashboardSidepanelIcon name={iconName} />
      <FormattedMessage id={labelId} />
    </NamedLink>
  );

  // Profile Settings nested link (Edit Profile, Availability, Pricing) - used inside Profile Settings section
  const profileSettingsNestedLink = (tab, labelId, selected, disabled) => {
    if (disabled) {
      return (
        <span
          key={tab}
          className={classNames(
            css.dashboardSidepanelNestedLink,
            css.dashboardSidepanelNestedLinkDisabled
          )}
          aria-disabled="true"
        >
          <FormattedMessage id={labelId} />
        </span>
      );
    }
    return (
      <NamedLink
        key={tab}
        name="EditListingPage"
        params={{
          ...editListingLinkParams,
          tab,
        }}
        className={classNames(css.dashboardSidepanelNestedLink, {
          [css.dashboardSidepanelNestedLinkActive]: selected,
        })}
      >
        <FormattedMessage id={labelId} />
      </NamedLink>
    );
  };

  const navButton = (tabKey, labelId, iconName) => {
    const isActive = activeTab === tabKey;
    return (
      <button
        key={tabKey}
        type="button"
        onClick={() => onTabChange(tabKey)}
        className={classNames(css.dashboardSidepanelButton, {
          [css.dashboardSidepanelButtonActive]: isActive,
        })}
        aria-pressed={isActive}
      >
        <DashboardSidepanelIcon name={iconName} />
        <FormattedMessage id={labelId} />
      </button>
    );
  };

  const dashboardTabLink = (tabKey, labelId, iconName, selected = false) => (
    <NamedLink
      key={tabKey}
      name="DashboardPage"
      className={classNames(css.dashboardSidepanelButton, css.dashboardSidepanelLink, {
        [css.dashboardSidepanelButtonActive]: selected,
      })}
    >
      <DashboardSidepanelIcon name={iconName} />
      <FormattedMessage id={labelId} />
    </NamedLink>
  );

  const linkItem = (name, labelId, iconName, selected) => (
    <NamedLink
      key={name}
      name={name}
      className={classNames(css.dashboardSidepanelButton, css.dashboardSidepanelLink, {
        [css.dashboardSidepanelButtonActive]: selected,
      })}
    >
      <DashboardSidepanelIcon name={iconName} />
      <FormattedMessage id={labelId} />
    </NamedLink>
  );

  const accountNestedLink = (name, labelId, selected) => (
    <NamedLink
      key={name}
      name={name}
      className={classNames(css.dashboardSidepanelNestedLink, {
        [css.dashboardSidepanelNestedLinkActive]: selected,
      })}
    >
      <FormattedMessage id={labelId} />
    </NamedLink>
  );

  const accountSubLinks = ACCOUNT_SUB_PAGES.filter(item => {
    if (item.providerOnly && !isProvider) return false;
    if (item.payoutOnly && !showAccountPayoutDetails) return false;
    if (item.paymentMethodsOnly && !showAccountPaymentMethods) return false;
    return true;
  }).map(item => {
    const selected =
      item.name === currentPage ||
      (item.name === 'StripePayoutPage' && currentPage === 'StripePayoutOnboardingPage');
    return accountNestedLink(item.name, item.labelId, selected);
  });

  const accountSection = isAccountSettingsActive ? (
    <div className={css.dashboardSidepanelGroup}>
      <div className={classNames(css.dashboardSidepanelButton, css.dashboardSidepanelButtonActive)}>
        <DashboardSidepanelIcon name="settings" />
        <FormattedMessage id="UserNav.accountSettings" />
      </div>
      <div className={css.dashboardSidepanelNested}>{accountSubLinks}</div>
    </div>
  ) : (
    linkItem('ContactDetailsPage', 'UserNav.accountSettings', 'settings', isAccountSettingsActive)
  );

  const messagesInboxTab = isProvider ? 'sales' : 'orders';
  const bookingsInboxTab = isProvider ? 'sales' : 'orders';
  const paymentsInboxTab = isProvider ? 'sales' : 'orders';
  const bookingsPageLink = (
    <NamedLink
      key="dashboard-bookings"
      name="BookingsPage"
      params={{ tab: bookingsInboxTab }}
      className={classNames(css.dashboardSidepanelButton, css.dashboardSidepanelLink, {
        [css.dashboardSidepanelButtonActive]: currentPage === 'BookingsPage',
      })}
    >
      <DashboardSidepanelIcon name="calendar" />
      <FormattedMessage id="DashboardPage.tabBookings" />
      {hasBookingsNotifications ? <span className={css.notificationDot} /> : null}
    </NamedLink>
  );
  const paymentsPageLink = (
    <NamedLink
      key="dashboard-payments"
      name="PaymentsPage"
      params={{ tab: paymentsInboxTab }}
      className={classNames(css.dashboardSidepanelButton, css.dashboardSidepanelLink, {
        [css.dashboardSidepanelButtonActive]: currentPage === 'PaymentsPage',
      })}
    >
      <DashboardSidepanelIcon name="dollar" />
      <FormattedMessage
        id={isProvider ? 'DashboardPage.tabEarnings' : 'DashboardPage.tabPayments'}
      />
    </NamedLink>
  );
  const inboxMessagesLink = (
    <NamedLink
      key="dashboard-messages"
      name="InboxPage"
      params={{ tab: messagesInboxTab }}
      className={classNames(css.dashboardSidepanelButton, css.dashboardSidepanelLink, {
        [css.dashboardSidepanelButtonActive]: currentPage === 'InboxPage',
      })}
    >
      <DashboardSidepanelIcon name="message" />
      <FormattedMessage id="DashboardPage.tabMessages" />
      {hasMessagesNotifications ? <span className={css.notificationDot} /> : null}
    </NamedLink>
  );

  const customerFirstGroup = useDashboardTabsAsLinks ? (
    <>
      {dashboardTabLink(DASHBOARD_TAB_KEYS.OVERVIEW, 'DashboardPage.tabOverview', 'home')}
      {bookingsPageLink}
      {paymentsPageLink}
      {inboxMessagesLink}
    </>
  ) : (
    <>
      {navButton(DASHBOARD_TAB_KEYS.OVERVIEW, 'DashboardPage.tabOverview', 'home')}
      {bookingsPageLink}
      {paymentsPageLink}
      {inboxMessagesLink}
    </>
  );

  const isOnEditListingPage = currentPage === 'EditListingPage';
  // The details tab is the default when no tab is given.
  const isEditTabActive = tab =>
    isOnEditListingPage && (currentEditTab === tab || (tab === 'details' && !currentEditTab));

  // Profile Settings section: when on Edit Listing page, show as expanded with sub-tabs (Edit Profile, Availability, Pricing)
  const profileSubLinks = EDIT_LISTING_TABS.map(({ tab, labelId }) => ({
    tab,
    labelId,
    selected: isEditTabActive(tab),
    disabled: tab !== 'details' && !!disabledEditTabs?.includes(tab),
  }));

  const profileSection = isOnEditListingPage ? (
    <div className={css.dashboardSidepanelGroup}>
      <div className={classNames(css.dashboardSidepanelButton, css.dashboardSidepanelButtonActive)}>
        <DashboardSidepanelIcon name="edit" />
        <FormattedMessage id="UserNav.profileSettings" />
      </div>
      <div className={css.dashboardSidepanelNested}>
        {profileSubLinks.map(({ tab, labelId, selected, disabled }) =>
          profileSettingsNestedLink(tab, labelId, selected, disabled)
        )}
      </div>
    </div>
  ) : (
    editListingLink('details', 'UserNav.profileSettings', 'edit', false)
  );

  const providerFirstGroup = useDashboardTabsAsLinks ? (
    <>
      {dashboardTabLink(DASHBOARD_TAB_KEYS_PROVIDER.OVERVIEW, 'DashboardPage.tabOverview', 'home')}
      {profileSection}
      {bookingsPageLink}
      {paymentsPageLink}
      {inboxMessagesLink}
    </>
  ) : (
    <>
      {navButton(DASHBOARD_TAB_KEYS_PROVIDER.OVERVIEW, 'DashboardPage.tabOverview', 'home')}
      {profileSection}
      {bookingsPageLink}
      {paymentsPageLink}
      {inboxMessagesLink}
    </>
  );

  const firstGroup = isProvider ? providerFirstGroup : customerFirstGroup;

  const isAdmin = isAdminUser(currentUser);
  const adminSection = isAdmin ? (
    <>
      <div className={css.dashboardSidepanelDivider} />
      <div className={css.dashboardSidepanelGroup}>
        {linkItem(
          'OnboardingPage',
          'UserNav.onboarding',
          'shield',
          currentPage === 'OnboardingPage'
        )}
        {linkItem(
          'AdminReviewsPage',
          'UserNav.adminReviews',
          'star',
          currentPage === 'AdminReviewsPage'
        )}
        {linkItem(
          'AdminListingsPage',
          'UserNav.adminListings',
          'tag',
          currentPage === 'AdminListingsPage'
        )}
      </div>
    </>
  ) : null;

  return (
    <div className={classes}>
      <nav
        className={css.dashboardSidepanelNav}
        aria-label={intl.formatMessage({ id: 'UserNav.screenreader.dashboardNav' })}
      >
        <div className={css.dashboardSidepanelGroup}>{firstGroup}</div>
        <div className={css.dashboardSidepanelDivider} />
        <div className={css.dashboardSidepanelGroup}>
          {!isProvider &&
            linkItem(
              'ProfileSettingsPage',
              'UserNav.profileSettings',
              'user',
              isProfileSettingsActive
            )}
          {accountSection}
        </div>
        {adminSection}
      </nav>
    </div>
  );
};

/**
 * A component that renders a navigation bar for user-specific pages.
 * - Default (topbar): horizontal tabs – Dashboard, Your listings, Profile settings, Account settings.
 * - variant="dashboardSidepanel": vertical sidebar for the dashboard page – Overview, Bookings, Payments, Messages (opens inbox), Settings (plus expert-only tabs).
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class for the root element
 * @param {string} [props.rootClassName] - Override default root class
 * @param {string} props.currentPage - Current page name (e.g. 'DashboardPage', 'ManageListingsPage')
 * @param {boolean} [props.showManageListingsLink] - Whether to show "Your listings" tab
 * @param {'horizontal'|'dashboardSidepanel'} [props.variant] - 'horizontal' = topbar, 'dashboardSidepanel' = dashboard sidebar (grouped)
 * @param {string} [props.activeTab] - Active in-page tab key (for variant="dashboardSidepanel")
 * @param {Function} [props.onTabChange] - Callback(tabKey) when tab is selected (for variant="dashboardSidepanel")
 * @param {string} [props.currentPage] - Current page name, used to highlight Profile/Account links (for variant="dashboardSidepanel")
 * @returns {JSX.Element}
 */
const UserNav = props => {
  const {
    className,
    rootClassName,
    currentPage,
    showManageListingsLink,
    variant = 'horizontal',
    activeTab,
    onTabChange,
    showAccountPayoutDetails,
    showAccountPaymentMethods,
    currentUser,
  } = props;
  const intl = useIntl();
  const config = useConfiguration();
  const roles = currentUser && config ? getCurrentUserTypeRoles(config, currentUser) : {};
  const isProvider = !!(currentUser && roles?.provider);

  if (variant === 'dashboardSidepanel') {
    return (
      <DashboardSidepanel
        activeTab={activeTab}
        onTabChange={onTabChange}
        currentPage={currentPage}
        className={className}
        rootClassName={rootClassName}
        showAccountPayoutDetails={showAccountPayoutDetails}
        showAccountPaymentMethods={showAccountPaymentMethods}
        currentUser={props.currentUser}
        editListingParams={props.editListingParams}
        currentEditTab={props.currentEditTab}
        disabledEditTabs={props.disabledEditTabs}
      />
    );
  }

  const manageListingsTabMaybe = showManageListingsLink
    ? [
        {
          text: <FormattedMessage id="UserNav.yourListings" />,
          selected: currentPage === 'DashboardPage',
          linkProps: { name: 'DashboardPage' },
        },
      ]
    : [];

  const tabs = [
    {
      text: <FormattedMessage id="UserNav.dashboard" />,
      selected: currentPage === 'DashboardPage',
      disabled: false,
      linkProps: { name: 'DashboardPage' },
    },
    ...manageListingsTabMaybe,
    // Experts use Edit Profile in Edit Listing Details; hide Profile settings tab.
    ...(!isProvider
      ? [
          {
            text: <FormattedMessage id="UserNav.profileSettings" />,
            selected: currentPage === 'ProfileSettingsPage',
            disabled: false,
            linkProps: { name: 'ProfileSettingsPage' },
          },
        ]
      : []),
    {
      text: <FormattedMessage id="UserNav.accountSettings" />,
      selected: ACCOUNT_SETTINGS_PAGES.includes(currentPage),
      disabled: false,
      linkProps: { name: 'ContactDetailsPage' },
    },
  ];

  const classes = classNames(rootClassName || css.root, className);
  return (
    <LinkTabNavHorizontal
      className={classes}
      tabRootClassName={css.tab}
      tabs={tabs}
      skin="dark"
      ariaLabel={intl.formatMessage({ id: 'UserNav.screenreader.userNav' })}
    />
  );
};

export default UserNav;
